import type { ComponentProps } from 'react';
import { AlertCircle, CheckCircle2, Circle, Clock, Loader2, TriangleAlert } from 'lucide-react';
import { cn } from '@/lib/utils';
import { isActiveStatus, statusLabel, statusTone, type StatusTone } from '@/lib/status';

const toneClass: Record<StatusTone, string> = {
  success: 'bg-status-success-soft text-status-success',
  warning: 'bg-status-warning-soft text-status-warning',
  danger: 'bg-status-danger-soft text-status-danger',
  info: 'bg-status-info-soft text-status-info',
  neutral: 'bg-status-neutral-soft text-status-neutral',
};
const toneIcon = {
  success: CheckCircle2, warning: TriangleAlert, danger: AlertCircle, info: Loader2, neutral: Circle,
} as const;

interface StatusBadgeProps extends Omit<ComponentProps<'span'>, 'children'> {
  status: string | null | undefined;
  label?: string;
}

// Standalone span, not built on ui/badge: shadcn Badge has no status variants and adds border/overflow-hidden.
export function StatusBadge({ status, label, className, ...props }: StatusBadgeProps) {
  const tone = statusTone(status);
  const active = tone === 'info' && isActiveStatus(status);
  const Icon = tone === 'info' && !active ? Clock : toneIcon[tone];
  return (
    <span
      {...props}
      data-tone={tone}
      className={cn('inline-flex h-5 items-center gap-1 rounded-full px-2 text-xs font-medium capitalize whitespace-nowrap', toneClass[tone], className)}
    >
      <Icon className={cn('size-3', active && 'animate-spin')} aria-hidden />
      {label ?? statusLabel(status)}
    </span>
  );
}
